import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  MAX_BALLS,
  MAX_PASSPHRASE_LENGTH,
  MAX_TITLE_LENGTH,
  MIN_BALLS,
  finalizeSlug,
  randomSlug,
} from '@photo-merge/shared';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { SlugInput } from '../components/SlugInput';
import { useSlugAvailability } from '../hooks/useSlugAvailability';
import { ArrangeStep } from '../create/ArrangeStep';
import { CropEditor } from '../create/CropEditor';
import { loadPhotos, type Photo } from '../create/photos';
import { renderBallSet } from '../create/export';
import { useSaveGame } from '../create/useSaveGame';
import { createPhotoArt } from '../game/art';
import { GameBoard } from '../game/GameBoard';
import type { CropTransform } from '../create/crop';
import styles from './Create.module.css';

type Stage = 'setup' | 'arrange' | 'play';

export function Create() {
  // Generated once on mount, so re-renders don't shuffle the suggestion under the user.
  const [slug, setSlug] = useState(randomSlug);
  const [title, setTitle] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [stage, setStage] = useState<Stage>('setup');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { progress, save } = useSaveGame();

  const status = useSlugAvailability(slug);
  const finalSlug = finalizeSlug(slug);

  async function handleFiles(list: FileList | null) {
    if (!list?.length) return;
    setBusy(true);
    setRejected([]);
    try {
      const room = MAX_BALLS - photos.length;
      const result = await loadPhotos([...list], room);
      setPhotos((current) => [...current, ...result.photos]);
      setRejected(result.rejected);
      if (result.photos.length) setStage('arrange');
    } finally {
      setBusy(false);
      // Clearing lets the same file be picked again after a removal.
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function commitCrop(crop: CropTransform) {
    setPhotos((current) =>
      current.map((photo, i) => (i === editing ? { ...photo, crop } : photo)),
    );
    setEditing(null);
  }

  async function handleSave() {
    const saved = await save(finalSlug, title, photos, passphrase);
    if (saved) navigate(`/play/${saved}`, { replace: true });
  }

  const enoughPhotos = photos.length >= MIN_BALLS;
  const canStart = status.kind === 'available';

  const art = useMemo(
    () => (stage === 'play' ? createPhotoArt(renderBallSet(photos)) : null),
    [stage, photos],
  );

  if (stage === 'play' && art) {
    const saving = progress.phase !== 'idle' && progress.phase !== 'error';
    return (
      <main className="page">
        <PageHeader
          title={title || 'Your game'}
          subtitle={`/play/${finalSlug} · not saved yet`}
          backTo="/create"
        />
        <GameBoard ballCount={photos.length} art={art} bestKey={`draft-${finalSlug}`} />

        <div className={styles.saveBar}>
          {progress.phase === 'error' ? (
            <p className={styles.warning}>{progress.message}</p>
          ) : null}
          {saving ? (
            <div className={styles.progress} role="status">
              <div className={styles.progressBar}>
                <span style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
              </div>
              <span className={styles.progressLabel}>{progress.message}</span>
            </div>
          ) : null}
          <Button block disabled={saving} onClick={() => void handleSave()}>
            {saving ? 'Saving…' : 'Save & get my link'}
          </Button>
          <Button
            variant="secondary"
            block
            disabled={saving}
            onClick={() => setStage('arrange')}
          >
            Back to editing
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <PageHeader title="Create a game" subtitle="Name it, then add your photos" />

      <div className={styles.steps}>
        <section className={styles.step}>
          <div className={styles.stepHead}>
            <span className={styles.stepNum}>1</span>
            <h2 className={styles.stepTitle}>Name your game</h2>
          </div>

          {/* The link comes first because it's the required field and the one that
              takes focus on arrival; the title is a nicety. */}
          <SlugInput value={slug} onChange={setSlug} status={status} autoSelect />

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Title (optional)</span>
            <input
              className={styles.text}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Grandma's 80th"
              maxLength={MAX_TITLE_LENGTH}
              enterKeyHint="done"
            />
          </label>

          <div className={styles.preview}>
            Your friends will play at
            <span className={styles.previewUrl}>
              {window.location.host}/play/{finalSlug || '…'}
            </span>
          </div>
        </section>

        <section className={styles.step}>
          <div className={styles.stepHead}>
            <span className={styles.stepNum}>2</span>
            <h2 className={styles.stepTitle}>
              Add photos{photos.length ? ` (${photos.length}/${MAX_BALLS})` : ''}
            </h2>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="visually-hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />

          {photos.length ? (
            <ArrangeStep
              photos={photos}
              onReorder={setPhotos}
              onEdit={setEditing}
              onRemove={(index) => setPhotos((current) => current.filter((_, i) => i !== index))}
            />
          ) : (
            <p className={styles.intro}>
              Pick {MIN_BALLS}–{MAX_BALLS} photos. They&apos;ll become balls that grow from
              smallest to biggest.
            </p>
          )}

          {rejected.length ? (
            <p className={styles.warning}>
              Couldn&apos;t read {rejected.length === 1 ? 'this photo' : 'these photos'}:{' '}
              {rejected.join(', ')}. If they came from an iPhone they may be HEIC — try
              re-picking, or screenshot them first.
            </p>
          ) : null}

          <div className={styles.tools}>
            <Button
              variant={photos.length ? 'secondary' : 'primary'}
              disabled={busy || photos.length >= MAX_BALLS}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? 'Reading…' : photos.length ? 'Add more' : 'Choose photos'}
            </Button>
            {photos.length ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setPhotos([]);
                  setRejected([]);
                }}
              >
                Clear all
              </Button>
            ) : null}
          </div>

          {/*
            Sits with the photos because the photos are what it protects. Blank is the
            default and the right answer for most games: the link is already the only
            way in, and a passphrase is one more thing to remember.
          */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Passphrase to edit these later (optional)</span>
            <input
              className={styles.text}
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Leave blank to let anyone edit"
              maxLength={MAX_PASSPHRASE_LENGTH}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="done"
            />
            <span className={styles.hint}>
              Asked for before the photo list can be changed. It can&apos;t be added or
              changed afterwards, so pick one now if you want one.
            </span>
          </label>
        </section>

        <div className={styles.actions}>
          <Button
            block
            disabled={!canStart || !enoughPhotos}
            onClick={() => setStage('play')}
          >
            {!enoughPhotos
              ? `Add at least ${MIN_BALLS} photos`
              : !canStart
                ? 'Pick an available link'
                : 'Try it out'}
          </Button>
        </div>
      </div>

      {editing != null && photos[editing] ? (
        <CropEditor
          photo={photos[editing]}
          rank={editing + 1}
          count={photos.length}
          onCommit={commitCrop}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </main>
  );
}
